import { DateTime } from "luxon"
import { RefObject, useMemo } from "react"
import DatePicker, { ReactDatePickerProps } from "react-datepicker"
import { convertLocalToUTCDate, convertUTCToLocalDate } from "./utils"

type LuxonInputProp = null | DateTime

export interface HighlightDates {
  [className: string]: DateTime[]
}

export interface LuxonDatePickerProps
  extends Omit<
    ReactDatePickerProps,
    | "startDate"
    | "endDate"
    | "selected"
    | "onChange"
    | "highlightDates"
    | "minDate"
    | "maxDate"
    | "excludeDates"
    | "customInputRef"
  > {
  startDate?: LuxonInputProp
  endDate?: LuxonInputProp
  selected?: LuxonInputProp
  highlightDates?: (HighlightDates | DateTime)[]
  minDate?: LuxonInputProp
  maxDate?: LuxonInputProp
  excludeDates?: (DateTime | null)[]
  /**
   * DateTime object is not compatible with react hook form. For details:
   * @see LuxonDatePicker.test.tsx
   */
  onChange: (date: null | DateTime) => void
  clearable?: boolean
  ariaLabels?: {
    openCalendar?: string
    clearCalendar?: string
  }
  datePickerRef?: RefObject<{ setFocus: () => void }>
}

/**
 * @description
 * react-datepicker does not support timezones, this component handles the conversions between UTC and local timezones.
 * We use Luxon in our apps which is configured to UTC by default so we use it here to handle the conversions.
 * react-datepicker only works with Date objects so we have to convert between types as well.
 *
 * 🚨
 * DateTime object is not compatible with react hook form. For details:
 * @see LuxonDatePicker.test.tsx
 */
export const LuxonDatePicker = ({
  startDate,
  datePickerRef,
  endDate,
  selected,
  onChange,
  dateFormat = "yyyy-MM-dd",
  highlightDates,
  minDate,
  maxDate,
  onBlur,
  disabled,
  clearable,
  ariaLabels: customAriaLabels,
  className,
  excludeDates,
  ...props
}: LuxonDatePickerProps) => {
  /**
   * converts highlighted dates from DateTime to Date objects.
   * accepts both arrays and objects - see storybook
   */
  const highlights = useMemo(() => {
    return highlightDates?.reduce((acc: ({ [className: string]: Date[] } | Date)[], item) => {
      if (DateTime.isDateTime(item)) {
        acc.push(convertUTCToLocalDate(item.toJSDate()))
      } else if (typeof item === "object") {
        const [[key, items]] = Object.entries(item)
        const convertedDates = items.map(date => convertUTCToLocalDate(date.toJSDate()))

        acc.push({ [key]: convertedDates })
      }

      return acc
    }, [])
  }, [highlightDates])

  /**
   * converts excludeDates  from DateTime to Date objects.
   * accepts arrays
   */
  const excludedDates = useMemo(() => {
    return excludeDates?.reduce((acc: Date[], item) => {
      if (DateTime.isDateTime(item)) acc.push(convertUTCToLocalDate(item.toJSDate()))
      return acc
    }, [])
  }, [excludeDates])

  return (
    <DatePicker
      // @ts-expect-error the ref support for React Datepicker is poor. This is the used to setFocus on the element. See https://github.com/Hacker0x01/react-datepicker/issues/276#issuecomment-1840477857
      ref={datePickerRef}
      startDate={startDate ? convertUTCToLocalDate(startDate?.toJSDate()) : null}
      endDate={endDate ? convertUTCToLocalDate(endDate.toJSDate()) : null}
      selected={selected ? convertUTCToLocalDate(selected.toJSDate()) : null}
      onChange={date =>
        date ? onChange(DateTime.fromJSDate(convertLocalToUTCDate(date))) : onChange(null)
      }
      dateFormat={dateFormat}
      highlightDates={highlights}
      minDate={minDate ? convertUTCToLocalDate(minDate?.toJSDate()) : null}
      maxDate={maxDate ? convertUTCToLocalDate(maxDate?.toJSDate()) : null}
      excludeDates={excludedDates}
      className={className}
      {...props}
    />
  )
}
